import { apiGet, apiPost } from './api-service';

// Types for Non-Compliance data
export interface NonComplianceIssue {
  id: string;
  templateId: string;
  templateName: string;
  instanceId: string | null;
  submissionId: string;
  questionId: string;
  questionText: string;
  nonCompliantOptionId: string | null;
  nonCompliantOptionText: string;
  correctiveActionIds: string[];
  correctiveActions: any[];
  otherCorrectiveAction: string | null;
  notes: string | null;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'assigned' | 'in_progress' | 'resolved' | 'overdue';
  assignedTo: string;
  assignedToName: string;
  assignedToEmail: string;
  siteId: string;
  siteName: string;
  clientId: string;
  clientName: string;
  dueDate: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByName: string;
  resolvedBy: string | null;
  resolvedByName?: string; // Added missing field from API
  resolvedAt?: string; // Added missing field from API
  resolutionNotes: string | null;
}

// Response interfaces for API calls
export interface NonComplianceIssuesResponse {
  message: string;
  errors: string[];
  content: {
    issues: NonComplianceIssue[];
    debug?: string;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

export interface KpiDataResponse {
  message: string;
  errors: string[];
  content: KpiData;
}

export interface KpiData {
  totalIssues: number;
  openIssues: number;
  inProgressIssues: number;
  resolvedIssues: number;
  closedIssues: number;
  overdueIssues: number;
  avgResolutionTimeHours: number;
  complianceRate: number;
  trendsLast30Days: {
    newIssues: number;
    resolvedIssues: number;
    complianceRateChange: number;
  };
  byPriority: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  bySite: Array<{
    siteId: string;
    siteName: string;
    totalIssues: number;
    openIssues: number;
    complianceRate: number;
  }>;
  byTemplate: Array<{
    templateId: string;
    templateName: string;
    totalIssues: number;
    complianceRate: number;
  }>;
}

export interface AssignIssueRequest {
  assignedTo: string;
  dueDate?: Date;
  notes?: string;
}

export interface ResolveIssueRequest {
  correctiveActions: string;
  resolutionDate?: string;
  resolvedBy?: string;
  evidenceUrls?: string[];
  notes?: string;
}

export interface BulkActionRequest {
  issueIds: string[];
  action: 'assign' | 'resolve' | 'delete' | 'update_priority';
  data: any;
}

// Employee interface for resolver dropdown
export interface SiteEmployee {
  id: string;
  client_id: string;
  title: string;
  full_name: string;
  name: string;
  surname: string;
  id_number: string | null;
  email: string;
  mobile: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  appointment_list: string | null;
  appointment: string[] | null;
  compliance_status: string | null;
  mapping_id: number;
  department: string | null;
  signature_s3_key: string | null;
  site: string;
  employee_number: string | null;
  next_of_kin_name: string | null;
  next_of_kin_surname: string | null;
  next_of_kin_cell: string | null;
  next_of_kin_email: string | null;
  gender: string | null;
}

export interface SiteEmployeeResponse {
  message: string;
  errors: string[];
  content: SiteEmployee[];
  totalRecords: number;
}

// API Service Functions

/**
 * Get KPI data for the non-compliance dashboard
 */
export async function getKpiData(siteId?: string, activeClientId?: string): Promise<KpiData> {
  try {
    console.log('Fetching KPI data from API...');
    const queryParams: any = { kpis: true, status: 'all' };
    if (siteId) {
      queryParams.siteId = siteId;
    }
    if (activeClientId) {
      queryParams.activeClientId = activeClientId;
    }
    const response = await apiGet('core', 'checklist-non-compliance-issues', {
      queryParams
    });
    console.log('KPI API response:', response);
    
    // Handle both direct content and wrapped responses
    const kpiData = response?.content || response;
    console.log('KPI Data received:', kpiData);
    
    return kpiData;
  } catch (e) {
    console.error('Failed to fetch KPI data:', e);
    // Return fallback empty KPI data
    return {
      totalIssues: 0,
      openIssues: 0,
      inProgressIssues: 0,
      resolvedIssues: 0,
      closedIssues: 0,
      overdueIssues: 0,
      avgResolutionTimeHours: 0,
      complianceRate: 0,
      trendsLast30Days: {
        newIssues: 0,
        resolvedIssues: 0,
        complianceRateChange: 0
      },
      byPriority: {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0
      },
      bySite: [],
      byTemplate: []
    };
  }
}

/**
 * Get filtered list of non-compliance issues
 */
export async function getNonComplianceIssues(params: {
  siteId?: string;
  activeClientId?: string;
  status?: string;
  priority?: string;
  priority_level?: string;  // Added to support backend API
  category?: string;
  assignedTo?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}): Promise<NonComplianceIssuesResponse> {
  try {
    const response = await apiGet('core', 'checklist-non-compliance-issues', {
      queryParams: params
    });
    return response;
  } catch (e: any) {
    console.error('Failed to fetch non-compliance issues:', e);
    // Return empty response when API fails - NO MOCK DATA
    return {
      message: "Failed to fetch non-compliance issues",
      errors: [e?.message || 'Unknown error'],
      content: {
        issues: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        }
      }
    };
  }
}

/**
 * Get specific non-compliance issue by ID
 */
export async function getNonComplianceIssue(id: string): Promise<NonComplianceIssue> {
  try {
    const response = await apiGet('core', `checklist-non-compliance-issues/${id}`);
    return response.content;
  } catch (e) {
    console.error(`Failed to fetch non-compliance issue ${id}:`, e);
    throw e;
  }
}

/**
 * Assign a non-compliance issue to someone
 */
export async function assignIssue(id: string, data: AssignIssueRequest): Promise<NonComplianceIssue> {
  try {
    const response = await apiPost('core', `checklist-non-compliance-issues/${id}/assign`, data);
    return response.content;
  } catch (e) {
    console.error(`Failed to assign issue ${id}:`, e);
    throw e;
  }
}

/**
 * Resolve a non-compliance issue
 */
export async function resolveIssue(id: string, data: ResolveIssueRequest): Promise<NonComplianceIssue> {
  try {
    const response = await apiPost('core', `checklist-non-compliance-issues/${id}/resolve`, data);
    return response.content;
  } catch (e) {
    console.error(`Failed to resolve issue ${id}:`, e);
    throw e;
  }
}

/**
 * Perform bulk actions on multiple issues
 */
export async function bulkAction(data: BulkActionRequest): Promise<{ success: boolean; processedIds: string[] }> {
  try {
    const response = await apiPost('core', 'checklist-non-compliance-issues', {
      queryParams: { bulkAction: 'true' },
      body: data
    });
    return response.content;
  } catch (e) {
    console.error('Failed to perform bulk action:', e);
    throw e;
  }
}

/**
 * Export non-compliance data
 */
export async function exportNonComplianceData(format: string = 'csv'): Promise<boolean> {
  try {
    // Fetch the JSON data first
    const response = await apiGet('core', 'checklist-non-compliance-issues', {
      queryParams: { export: 'true', format }
    });
    
    // Extract issues from the response
    const issues = response?.content?.issues || response?.issues || [];
    
    if (!issues.length) {
      throw new Error('No data to export');
    }
    
    // Convert to CSV format with human-readable columns
    const csvData = convertToCsv(issues);
    
    // Create blob and download
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `non-compliance-issues-${new Date().toISOString().split('T')[0]}.csv`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (e) {
    console.error('Failed to export non-compliance data:', e);
    throw e;
  }
}

/**
 * Convert issues array to CSV format with human-readable columns
 */
function convertToCsv(issues: NonComplianceIssue[]): string {
  // Define the columns we want to export (human-readable and useful fields)
  const headers = [
    'Issue ID',
    'Template Name', 
    'Question Text',
    'Non-Compliant Option',
    'Priority',
    'Status',
    'Assigned To',
    'Site Name',
    'Client Name',
    'Due Date',
    'Created Date',
    'Updated Date',
    'Created By',
    'Resolved By',
    'Resolved Date',
    'Notes',
    'Other Corrective Action',
    'Resolution Notes'
  ];
  
  // Create CSV rows
  const rows = issues.map(issue => [
    issue.id,
    issue.templateName || '',
    issue.questionText || '',
    issue.nonCompliantOptionText || '',
    issue.priority || '',
    issue.status || '',
    issue.assignedToName || 'Unassigned',
    issue.siteName || '',
    issue.clientName || '',
    formatDate(issue.dueDate) || '',
    formatDate(issue.createdAt) || '',
    formatDate(issue.updatedAt) || '',
    issue.createdByName || '',
    issue.resolvedByName || '', // Now using the correct field
    formatDate(issue.resolvedAt) || '', // Now using the correct field
    issue.notes || '',
    issue.otherCorrectiveAction || '',
    issue.resolutionNotes || ''
  ]);
  
  // Combine headers and rows
  const allRows = [headers, ...rows];
  
  // Convert to CSV string
  return allRows
    .map(row => 
      row.map(field => {
        // Escape quotes and wrap in quotes if contains comma, newline, or quote
        const stringField = String(field || '');
        if (stringField.includes(',') || stringField.includes('\n') || stringField.includes('"')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      }).join(',')
    )
    .join('\n');
}

/**
 * Format date string to readable format
 */
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  } catch {
    return dateString;
  }
}

/**
 * Get site employees for resolver dropdown, optionally filtered by site ID
 */
export async function getSiteEmployees(siteId?: string): Promise<SiteEmployee[]> {
  try {
    const queryParams: any = {
      first: '0',
      rows: '9999999',
      page: '0',
      globalSearch: '',
      sortField: 'created_at',
      sortOrder: '-1',
      filters: '{}',
      col: 'full_name,title,appointment_list,id_number,compliance_status,active',
      table_view: 'true'
    };

    // Add site filtering if siteId is provided
    if (siteId) {
      queryParams.activeSiteId = siteId;
    }

    const response = await apiGet('core', 'site-employee', {
      queryParams
    });
    return response.content || [];
  } catch (e) {
    console.error('Failed to fetch site employees:', e);
    return [];
  }
}